import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ClipboardEvent,
  FormEvent,
  KeyboardEvent,
  MouseEvent
} from 'react'

import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { replaceTranslationVariables } from 'utils/text-transformation'
import { getMentionQuery, splitMentionSegments } from 'utils/promptMentions'
import AnimatedPlaceholder from '../AnimatedPlaceholder'
import MentionPicker from '../MentionPicker'
import {
  CHIP_CLASS,
  CHIP_REMOVE_CLASS,
  MENTION_EDITOR_CLASS,
  MENTION_TOKEN_RESERVE
} from '../consts'
import * as Styled from './styled'
import { IInsertTrigger, IPickerState, IProps } from './types'

/**
 * Shared AI prompt input (AI Image + AI Video): a contenteditable editor whose
 * VALUE CONTRACT is plain text with `@Image N` tokens (identical to the
 * textarea it replaces), while tokens render as interactive chips — × removes
 * the mention, clicking a chip re-opens the picker to swap the referenced
 * image. Typing `@` opens the picker at the caret.
 *
 * Tab-agnostic by construction: it receives already-numbered `options` and
 * knows nothing about either store slice. An empty `options` array makes `@`
 * inert, which is how AI Video disables mentions for the frames bucket and for
 * models without multi-reference support.
 *
 * Content is managed imperatively (React must not fight user edits);
 * `serializeEditor` <-> `splitMentionSegments` keep the DOM and the text value
 * in sync.
 *
 * NOTE: v-editor renders in plain DOM, so caret access uses
 * `window.getSelection()` directly. The sibling widgets
 * (ai-image-generator / ai-video-generator) mount inside a shadow root and
 * need a much larger caret stack there (ShadowRoot.getSelection →
 * Selection.getComposedRanges → beforeinput.getTargetRanges); copy it from
 * them if this app is ever embedded in a shadow root.
 */
const PromptEditor = ({
  value,
  onChange,
  placeholderTexts,
  isPlaceholderActive = true,
  staticPlaceholder,
  maxLength,
  hasError,
  minHeight,
  maxHeight,
  maxHeightMobile,
  options,
  onFocus,
  onBlur,
  'data-testid': dataTestId
}: IProps) => {
  const editorRef = useRef<HTMLDivElement | null>(null)
  const lastEmittedRef = useRef<string | null>(null)
  const triggerRef = useRef<IInsertTrigger | null>(null)
  const editChipRef = useRef<HTMLElement | null>(null)
  // Trigger dismissed with Escape — stays closed while it is still the active
  // trigger.
  const dismissedRef = useRef<{ node: Text; startOffset: number } | null>(null)

  const [picker, setPicker] = useState<IPickerState | null>(null)
  const [highlightedState, setHighlightedState] = useState(0)
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

  // --- caret helpers ---

  const getCaretRange = useCallback(() => {
    const editor = editorRef.current
    const selection = window.getSelection()

    if (!editor || !selection?.rangeCount) {
      return null
    }

    const range = selection.getRangeAt(0)

    if (!editor.contains(range.startContainer)) {
      return null
    }

    return range
  }, [])

  // setBaseAndExtent (not addRange) so a collapsed caret is set in one call.
  const setCaret = useCallback((node: Node, offset: number) => {
    window.getSelection()?.setBaseAndExtent(node, offset, node, offset)
  }, [])

  const isEditorFocused = useCallback(
    () => document.activeElement === editorRef.current,
    []
  )

  const placeCaretAfter = useCallback(
    (node: Node) => {
      const parent = node.parentNode

      if (!parent) {
        return
      }

      const index = Array.prototype.indexOf.call(parent.childNodes, node)
      setCaret(parent, index + 1)
    },
    [setCaret]
  )

  // --- chip DOM <-> text value ---

  const chipLabel = useCallback(
    (imageNumber: number) =>
      replaceTranslationVariables(translate(T.MENTION_IMAGE_LABEL), [
        String(imageNumber)
      ]),
    []
  )

  const createChip = useCallback(
    (imageNumber: number): HTMLElement => {
      const chip = document.createElement('span')
      chip.className = CHIP_CLASS
      chip.contentEditable = 'false'
      chip.dataset.mentionNumber = String(imageNumber)
      chip.setAttribute('data-testid', 'mention-chip')

      const label = document.createElement('span')
      label.textContent = chipLabel(imageNumber)
      chip.appendChild(label)

      const remove = document.createElement('button')
      remove.type = 'button'
      remove.tabIndex = -1
      remove.className = CHIP_REMOVE_CLASS
      remove.setAttribute('aria-label', 'Remove mention')
      remove.textContent = '×'
      chip.appendChild(remove)

      return chip
    },
    [chipLabel]
  )

  const serializeEditor = useCallback((): string => {
    const editor = editorRef.current

    if (!editor) {
      return ''
    }

    let out = ''

    const walk = (node: Node) => {
      node.childNodes.forEach(child => {
        if (child.nodeType === Node.TEXT_NODE) {
          out += child.textContent ?? ''
          return
        }

        if (!(child instanceof HTMLElement)) {
          return
        }

        if (child.dataset.mentionNumber) {
          out += `@Image ${child.dataset.mentionNumber}`
          // A digit directly after the chip (user deleted the separating
          // space) would merge into the token ("@Image 1" + "5" -> image 15)
          // — keep the serialized token delimited.
          const nextText =
            child.nextSibling?.nodeType === Node.TEXT_NODE
              ? (child.nextSibling.textContent ?? '')
              : ''

          if (/^\d/.test(nextText)) {
            out += ' '
          }

          return
        }

        if (child.tagName === 'BR') {
          out += '\n'
          return
        }

        // Browsers wrap Enter-created lines in block elements (usually
        // <div>) — represent the block boundary as a newline.
        if (out && !out.endsWith('\n')) {
          out += '\n'
        }

        walk(child)
      })
    }

    walk(editor)

    return out
  }, [])

  const renderValue = useCallback(
    (text: string) => {
      const editor = editorRef.current

      if (!editor) {
        return
      }

      editor.innerHTML = ''
      splitMentionSegments(text).forEach(segment => {
        if (segment.type === 'text') {
          editor.appendChild(document.createTextNode(segment.value))
        } else {
          editor.appendChild(createChip(segment.imageNumber))
        }
      })
    },
    [createChip]
  )

  const closePicker = useCallback(() => {
    setPicker(null)
    triggerRef.current = null
    editChipRef.current = null
  }, [])

  // Native contenteditable behavior is kept as-is: deleting all content leaves
  // a stray <br> behind (serialized to '\n', so the value never returns to ''
  // and the animated placeholder does not reappear after typing) — a
  // deliberate trade-off; DOM normalization here caused caret/Enter
  // regressions in the sibling repos.
  const emit = useCallback(() => {
    if (!editorRef.current) {
      return
    }

    const text = serializeEditor()
    lastEmittedRef.current = text
    onChange(text)
  }, [serializeEditor, onChange])

  // External value changes (mention renumbering after a reference-image
  // removal, "Create similar", regeneration form) re-render the chips; our own
  // emits are skipped.
  useEffect(() => {
    if (value === lastEmittedRef.current) {
      return
    }

    renderValue(value)
    lastEmittedRef.current = value
    closePicker()

    const editor = editorRef.current

    if (editor && isEditorFocused()) {
      setCaret(editor, editor.childNodes.length)
    }
  }, [value, renderValue, closePicker, isEditorFocused, setCaret])

  // --- trigger detection ---

  const getAnchorPosition = useCallback((rect: DOMRect) => {
    const wrapperRect =
      editorRef.current?.parentElement?.getBoundingClientRect()

    if (!wrapperRect) {
      return { anchorTop: 0, anchorLeft: 0 }
    }

    return {
      anchorTop: rect.bottom - wrapperRect.top,
      anchorLeft: rect.left - wrapperRect.left
    }
  }, [])

  const detectInsertTrigger = useCallback((): IInsertTrigger | null => {
    const caretRange = getCaretRange()

    if (!caretRange?.collapsed) {
      return null
    }

    const { startContainer: anchorNode, startOffset: anchorOffset } = caretRange

    if (anchorNode.nodeType !== Node.TEXT_NODE) {
      return null
    }

    // The trigger grammar lives in ONE place — the pure helper the
    // characterization tests pin. Only the DOM lookup is local: within a text
    // node, node-start counts as a boundary (start of text / right after a
    // chip), matching getMentionQuery's start-of-text rule.
    const mentionQuery = getMentionQuery({
      text: anchorNode.textContent ?? '',
      caretIndex: anchorOffset
    })

    if (!mentionQuery) {
      return null
    }

    return {
      node: anchorNode as Text,
      startOffset: mentionQuery.startIndex,
      endOffset: anchorOffset,
      query: mentionQuery.query
    }
  }, [getCaretRange])

  const refreshInsertPicker = useCallback(() => {
    // Never override an open chip-edit picker from caret movements — unless
    // the chip itself was deleted (e.g. Backspace), which leaves the edit
    // picker pointing at a detached node: close it and fall through.
    if (editChipRef.current) {
      if (editChipRef.current.isConnected) {
        return
      }

      closePicker()
    }

    const trigger = detectInsertTrigger()

    if (!trigger) {
      dismissedRef.current = null
      triggerRef.current = null
      setPicker(prev => (prev?.mode === 'insert' || !prev ? null : prev))
      return
    }

    const dismissed = dismissedRef.current

    if (
      dismissed &&
      dismissed.node === trigger.node &&
      dismissed.startOffset === trigger.startOffset
    ) {
      triggerRef.current = null
      setPicker(null)
      return
    }

    const range = document.createRange()
    range.setStart(trigger.node, trigger.startOffset)
    range.setEnd(trigger.node, trigger.endOffset)

    const { anchorTop, anchorLeft } = getAnchorPosition(
      range.getBoundingClientRect()
    )

    triggerRef.current = trigger
    setPicker(prev => {
      if (!prev || prev.query !== trigger.query) {
        setHighlightedState(0)
      }

      return { mode: 'insert', anchorTop, anchorLeft, query: trigger.query }
    })
  }, [detectInsertTrigger, getAnchorPosition, closePicker])

  // Caret moves without input (clicks, arrow keys) also open/close the picker.
  useEffect(() => {
    const handleSelectionChange = () => {
      if (isEditorFocused()) {
        refreshInsertPicker()
      }
    }

    document.addEventListener('selectionchange', handleSelectionChange)

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
    }
  }, [refreshInsertPicker, isEditorFocused])

  // --- picker options / keyboard navigation ---

  const filteredOptions = useMemo(() => {
    if (picker?.mode !== 'insert' || !picker.query) {
      return options
    }

    return options.filter(option =>
      String(option.imageNumber).startsWith(picker.query)
    )
  }, [options, picker])

  const isPickerOpen = picker !== null && filteredOptions.length > 0

  // Every listed option is selectable (the owning tab omits the rest), so the
  // highlight only has to stay in range as the filtered list shrinks.
  const highlightedIndex =
    highlightedState < filteredOptions.length ? highlightedState : 0

  const moveHighlight = useCallback(
    (direction: 1 | -1) => {
      if (!filteredOptions.length) {
        return
      }

      setHighlightedState(prev => {
        const current = prev < filteredOptions.length ? prev : 0

        return (
          (current + direction + filteredOptions.length) %
          filteredOptions.length
        )
      })
    },
    [filteredOptions.length]
  )

  // --- mention mutations ---

  const insertMentionAtTrigger = useCallback(
    (imageNumber: number) => {
      const trigger = triggerRef.current
      const editor = editorRef.current

      if (!trigger || !editor) {
        return
      }

      if (
        maxLength &&
        serializeEditor().length + MENTION_TOKEN_RESERVE > maxLength
      ) {
        return
      }

      const range = document.createRange()
      range.setStart(trigger.node, trigger.startOffset)
      range.setEnd(trigger.node, trigger.endOffset)
      range.deleteContents()

      const chip = createChip(imageNumber)
      range.insertNode(chip)
      // The trailing space keeps the serialized token delimited (a digit typed
      // right after the chip must not merge into the token).
      const space = document.createTextNode(' ')
      chip.after(space)

      editor.focus()
      placeCaretAfter(space)
      closePicker()
      emit()
    },
    [maxLength, serializeEditor, createChip, placeCaretAfter, closePicker, emit]
  )

  const applyChipEdit = useCallback(
    (imageNumber: number) => {
      const chip = editChipRef.current

      if (!chip?.isConnected) {
        // Chip deleted (Backspace) while its edit picker was open.
        closePicker()
        return
      }

      chip.dataset.mentionNumber = String(imageNumber)
      const label = chip.querySelector('span')

      if (label) {
        label.textContent = chipLabel(imageNumber)
      }

      closePicker()
      emit()
    },
    [chipLabel, closePicker, emit]
  )

  const removeChip = useCallback(
    (chip: HTMLElement) => {
      // Also swallow ONE following space so no double space remains — same
      // rule as the pure remapMentions strip.
      const next = chip.nextSibling

      if (
        next?.nodeType === Node.TEXT_NODE &&
        next.textContent?.startsWith(' ')
      ) {
        next.textContent = next.textContent.slice(1)
      }

      chip.remove()

      if (editChipRef.current === chip) {
        closePicker()
      }

      emit()
    },
    [closePicker, emit]
  )

  const handlePickerSelect = useCallback(
    (imageNumber: number) => {
      if (picker?.mode === 'edit') {
        applyChipEdit(imageNumber)
      } else {
        insertMentionAtTrigger(imageNumber)
      }
    },
    [picker, applyChipEdit, insertMentionAtTrigger]
  )

  // --- editor events ---

  const handleInput = useCallback(() => {
    emit()
    refreshInsertPicker()
  }, [emit, refreshInsertPicker])

  const handleEditorClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement

      const removeButton = target.closest(`.${CHIP_REMOVE_CLASS}`)

      if (removeButton) {
        event.preventDefault()
        const chip = removeButton.closest(`.${CHIP_CLASS}`)

        if (chip instanceof HTMLElement) {
          removeChip(chip)
        }

        return
      }

      const chip = target.closest(`.${CHIP_CLASS}`)

      if (chip instanceof HTMLElement) {
        event.preventDefault()

        // Nothing to swap to: opening an edit picker here would strand
        // editChipRef, and because refreshInsertPicker bails while that ref is
        // set, `@` could never reopen the picker until blur.
        if (!options.length) {
          return
        }

        editChipRef.current = chip
        triggerRef.current = null

        const { anchorTop, anchorLeft } = getAnchorPosition(
          chip.getBoundingClientRect()
        )

        // Seed by LOOKUP, not `imageNumber - 1`: the owning tab omits images that
        // cannot be referenced, so an option's index no longer equals its number.
        const chipNumber = Number(chip.dataset.mentionNumber)
        const seeded = options.findIndex(
          option => option.imageNumber === chipNumber
        )

        setHighlightedState(seeded === -1 ? 0 : seeded)
        setPicker({ mode: 'edit', anchorTop, anchorLeft, query: '' })
      }
    },
    [removeChip, getAnchorPosition, options]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!isPickerOpen) {
        return
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        moveHighlight(event.key === 'ArrowDown' ? 1 : -1)
        return
      }

      if (event.key === 'Enter') {
        const highlighted = filteredOptions[highlightedIndex]

        if (highlighted) {
          event.preventDefault()
          handlePickerSelect(highlighted.imageNumber)
        }

        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        const trigger = triggerRef.current

        if (picker?.mode === 'insert' && trigger) {
          dismissedRef.current = {
            node: trigger.node,
            startOffset: trigger.startOffset
          }
        }

        closePicker()
      }
    },
    [
      isPickerOpen,
      moveHighlight,
      filteredOptions,
      highlightedIndex,
      handlePickerSelect,
      picker,
      closePicker
    ]
  )

  const handleBeforeInput = useCallback(
    (event: FormEvent<HTMLDivElement>) => {
      if (!maxLength) {
        return
      }

      const inputType = (event.nativeEvent as InputEvent).inputType ?? ''

      if (
        inputType.startsWith('insert') &&
        serializeEditor().length >= maxLength
      ) {
        event.preventDefault()
      }
    },
    [maxLength, serializeEditor]
  )

  // Blurring always dismisses the picker; the owning tab may also want to know
  // (AI Video hides its placeholder while focused).
  const handleBlur = useCallback(() => {
    closePicker()
    onBlur?.()
  }, [closePicker, onBlur])

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault()

      const caretRange = getCaretRange()

      if (!caretRange) {
        return
      }

      const pasted = event.clipboardData.getData('text')
      const room = maxLength
        ? Math.max(0, maxLength - serializeEditor().length)
        : Infinity
      const text = pasted.slice(0, room === Infinity ? undefined : room)

      if (!text) {
        return
      }

      caretRange.deleteContents()
      const node = document.createTextNode(text)
      caretRange.insertNode(node)
      placeCaretAfter(node)
      emit()
      refreshInsertPicker()
    },
    [
      maxLength,
      serializeEditor,
      placeCaretAfter,
      emit,
      refreshInsertPicker,
      getCaretRange
    ]
  )

  return (
    <Styled.EditorWrapper>
      <Styled.Editor
        ref={editorRef}
        className={MENTION_EDITOR_CLASS}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        $minHeight={minHeight}
        $maxHeight={maxHeight}
        $maxHeightMobile={maxHeightMobile}
        $isError={hasError}
        data-testid={dataTestId}
        onInput={handleInput}
        onClick={handleEditorClick}
        onKeyDown={handleKeyDown}
        onBeforeInput={handleBeforeInput}
        onPaste={handlePaste}
        onFocus={onFocus}
        onBlur={handleBlur}
      />
      {!value &&
        (isPlaceholderActive && placeholderTexts?.length ? (
          <AnimatedPlaceholder texts={placeholderTexts} />
        ) : (
          !!staticPlaceholder && (
            <Styled.PlaceholderOverlay>
              {staticPlaceholder}
            </Styled.PlaceholderOverlay>
          )
        ))}
      {!!picker && (
        // Remounted per caret position: useRelativeCoords recomputes on anchor
        // IDENTITY, not on a moved anchor's rect.
        <Styled.PickerAnchor
          key={`${picker.anchorTop}:${picker.anchorLeft}`}
          ref={setAnchorEl}
          style={{ top: picker.anchorTop, left: picker.anchorLeft }}
        />
      )}
      <MentionPicker
        isOpen={isPickerOpen}
        anchorEl={anchorEl}
        options={filteredOptions}
        highlightedIndex={highlightedIndex}
        onHighlight={setHighlightedState}
        onSelect={handlePickerSelect}
        onClose={closePicker}
      />
    </Styled.EditorWrapper>
  )
}

export default memo(PromptEditor)
